import getCertInfoFromServer from 'owa-smime-adapter/lib/services/getCertInfoFromServer';
import getSmimeAttachmentType from './getSmimeAttachmentType';
import SmimeCustomError from './SmimeCustomError';
import mergeSmimeDecodedProperties from '../actions/mergeSmimeDecodedProperties';
import onSmimeItemDecodeFailed from '../actions/onSmimeItemDecodeFailed';
import ISmimeAdapter from 'owa-smime-adapter/lib/adapter/ISmimeAdapter';
import { lazySmimeAdapter } from 'owa-smime-adapter';
import fetchAndDecodeSmimeMessage from '../services/fetchAndDecodeSmimeMessage';
import SmimeMessage from 'owa-smime-adapter/lib/store/schema/SmimeMessage';
import X509ChainStatusFlags from '../store/schema/X509ChainStatusFlags';
import isSmimeAdapterUsable from 'owa-smime-adapter/lib/utils/isSmimeAdapterUsable';
import { ClientItem } from 'owa-mail-store';
import SmimeErrorStateEnum from 'owa-smime-adapter/lib/store/schema/SmimeErrorStateEnum';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import ItemId from 'owa-service/lib/contract/ItemId';
import { trace } from 'owa-trace';

/**
 * Fetch and decode SMIME information from the smimeAttachment and update the NormalizedBody and other relevant attributes in the item
 * @param itemId
 */
const trySetSmimeProperties = async (itemId: ItemId) => {
    const cachedItem: ClientItem = itemId?.Id && getMailStore().items.get(itemId.Id);
    if (!cachedItem) {
        return;
    }

    try {
        const adapter: ISmimeAdapter = await lazySmimeAdapter.import();
        if (isSmimeAdapterUsable()) {
            const smimeAttachment: AttachmentType = getSmimeAttachmentType(cachedItem);

            /**
             * This check should be under isSmimeAdapterUsable() because
             * if isSmimeAdapterUsable is false then smimeAttachment may not
             * exist because in case of Digital Sign server decodes it and p7m information is
             * not present in attachment and it will always throw
             * SmimeErrorStateEnum.CannotFetchDataPackage error which is not desirable.
             */
            if (!smimeAttachment?.AttachmentId) {
                throw new SmimeCustomError(
                    'The S/MIME data package information is not available in item.',
                    SmimeErrorStateEnum.CannotFetchDataPackage
                );
            }

            const smimeMessage: SmimeMessage = await fetchAndDecodeSmimeMessage(
                smimeAttachment,
                cachedItem.MailboxInfo.mailboxSmtpAddress,
                adapter
            );
            if (smimeMessage.SmimeSignature && !cachedItem.IsDraft) {
                const certInfo = await getCertInfoFromServer(
                    smimeMessage.SmimeSignature.CertRawData,
                    false /* isSend */
                );
                if (certInfo?.IsValid) {
                    smimeMessage.SmimeSignature.IsCertValidToServer = true;
                    smimeMessage.SmimeSignature.ServerVerificationResult = 0;
                    trace.info('S/MIME: Digital Signature verified from server');
                } else {
                    smimeMessage.SmimeSignature.IsCertValidToServer = false;
                    smimeMessage.SmimeSignature.ServerVerificationResult = certInfo
                        ? certInfo.PolicyFlag
                        : X509ChainStatusFlags.CertificateDataCorrupted;
                    trace.error(
                        'SmimeTraceError: Digital Signature verification failed. ServerVerificationResult: ' +
                            certInfo?.PolicyFlag
                    );
                }
            }
            mergeSmimeDecodedProperties(itemId, smimeMessage);
        }
    } catch (error) {
        if (error.errorCode) {
            // SmimeCustomError
            onSmimeItemDecodeFailed(itemId, error.errorCode);
        } else {
            // This is unhandled error and we don't know the
            // cause of this error.
            trace.error('SmimeTraceError: ' + error.message);
            onSmimeItemDecodeFailed(itemId, SmimeErrorStateEnum.UnknownError);
        }
    }
};

export default trySetSmimeProperties;
